import {Icon, IconButton} from '@wandb/components';
import {TargetBlank} from '@wandb/weave/common/util/links';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import React, {useState} from 'react';
import {twMerge} from 'tailwind-merge';

import {type NamedWorkspaceMenuItem, type SortOption} from '../types';
import {WANDB_WORKSPACES_DOCS} from '../utils';
import {NamedWorkspaceMenuGroup} from './NamedWorkspaceMenuGroup';
import {NamedWorkspaceSortOptionSelector} from './NamedWorkspaceSortOptionSelector';

type NamedWorkspaceViewSectionProps = {
  title: string;
  infoText: string;
  items: NamedWorkspaceMenuItem[];
  totalCount: number;
  isFiltered: boolean;
  testId: string;
  sortOption: SortOption;
  onSelectSortOption: (sortOption: SortOption) => void;
};

export const NamedWorkspaceViewSection = ({
  title,
  infoText,
  items,
  totalCount,
  isFiltered,
  testId,
  sortOption,
  onSelectSortOption,
}: NamedWorkspaceViewSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [shouldShowInfo, setShouldShowInfo] = useState(false);

  if (totalCount === 0) {
    return null;
  }

  return (
    <DropdownMenu.Label className="mb-8 mt-16 flex flex-col gap-8 px-12">
      <div className="flex items-center justify-between gap-16">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-6 text-left text-[15px] font-semibold leading-[24px]"
          data-test={testId}
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded(previous => !previous)}>
          <Icon icon={isExpanded ? 'chevron-down' : 'chevron-next'} size="sm" />
          <span>{title}</span>
          <div className="rounded bg-wb-surface-sunken px-6 text-xs">
            {isFiltered ? `${items.length} of ${totalCount}` : totalCount}
          </div>
        </button>
        <div className="flex items-center justify-center rounded text-wb-text-tertiary">
          <IconButton
            variant="ghost"
            icon="info"
            size="sm"
            aria-label={`${title} information`}
            onPress={() => {
              setShouldShowInfo(previous => (isExpanded ? !previous : true));
              setIsExpanded(true);
            }}
          />
          <NamedWorkspaceSortOptionSelector
            selectedOption={sortOption}
            onSelectOption={onSelectSortOption}
            ariaLabel={`${title} options`}
          />
        </div>
      </div>
      {isExpanded && (
        <div className="contents">
          {shouldShowInfo && (
            <p
              className={twMerge(
                'h-full w-full rounded px-12 py-8 text-sm',
                'bg-wb-surface-sunken text-wb-text-tertiary dark:bg-moonbeam/[.05]'
              )}>
              {infoText}{' '}
              <span>
                <TargetBlank
                  href={WANDB_WORKSPACES_DOCS}
                  className="font-semibold text-wb-text-link hover:text-wb-text-link-hover">
                  Learn more about views
                </TargetBlank>
              </span>{' '}
              in Docs.
            </p>
          )}
          {items.length > 0 && (
            <NamedWorkspaceMenuGroup
              options={items}
              defaultMaxItems={5}
              testId={testId}
            />
          )}
          {isFiltered && items.length === 0 && (
            <div
              className={twMerge(
                'flex justify-center px-12 py-16 text-center',
                'w-full text-sm text-wb-text-tertiary'
              )}>
              <p>No matching views.</p>
            </div>
          )}
        </div>
      )}
    </DropdownMenu.Label>
  );
};
